import React from 'react';
import { ReactComponent as LogoSvg } from '@/assets/logo.svg';
import moment from 'moment-timezone';
import { BoardType } from './constants';

export const APP_ENV: 'STAGING' | 'PRODUCTION' = (process.env.REACT_APP_ENV as any) || 'STAGING';

export const AppEnvSetting = {
  eScottUrl: process.env.REACT_APP_ESCOTT_URL,
  enableEScott: process.env.REACT_APP_ESCOTT_ENABLE === 'yes',
  disableSso: process.env.REACT_APP_DISABLE_SSO === 'yes',
};
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_GET_ALL_PAGE_SIZE = 500;

export const HIDE_MESSAGE_401_PARAMS = '___No_Display_Error_Message';
export const UNKNOWN_ERROR_MESSAGE = 'UNKNOWN_ERROR_MESSAGE';

export const AppLogo = (props: React.SVGProps<any>) => {
  return <LogoSvg {...props} />;
};

export const EMAIL_REGEX = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
export const PHONE_NUMBER_REGEX = /^[0-9\-\+]{6,}$/;
export const ROMANJI_CHARACTER_REGEX = /^[a-zA-Z ]*$/;

export const PASSWORD_REGEX = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

export const SearchBoxBackgroundImage = require('@/assets/bg/home-jp.jpg');
export const DATE_ONLY_FORMAT = 'YYYY-MM-DD';

export const formatDateOnly = (value: any) => {
  return moment(value).format(DATE_ONLY_FORMAT);
};

export const formatDateTime = (value: any) => {
  return moment(value).format(DATE_ONLY_FORMAT);
};

export const DATE_ONLY_DISPLAY_FORMAT = 'DD MMM YYYY';
export const DATE_TIME_DISPLAY_FORMAT = 'DD MMM YYYY HH:mm';
export const DATE_TIME_JST_DISPLAY_FORMAT = 'DD MMM YYYY HH:mm [JST]';

export const DATE_JST_DISPLAY_FORMAT = 'DD MMM YYYY [JST]';

export const TIME_ONLY_DISPLAY_FORMAT = 'HH:mm';

export const appDisplayDateFormat = {
  default: {
    dateOnly: (value: any) => {
      if (value) {
        return moment(value).format(DATE_ONLY_DISPLAY_FORMAT);
      }
      return '';
    },
    withTime: (value: any) => {
      if (value) {
        return moment(value).format(DATE_TIME_DISPLAY_FORMAT);
      }
      return '';
    },
    withJstDateTime: (value: any) => {
      if (value) {
        return moment(value)
          .tz('Asia/Tokyo')
          .format(DATE_TIME_JST_DISPLAY_FORMAT);
      }
      return '';
    },
    withJstTimeOnly: (value: any) => {
      if (value) {
        return moment(value)
          .tz('Asia/Tokyo')
          .format(TIME_ONLY_DISPLAY_FORMAT);
      }
      return '';
    },
    withJstDateOnly: (value: any) => {
      if (value) {
        return moment(value)
          .tz('Asia/Tokyo')
          .format(DATE_JST_DISPLAY_FORMAT);
      }
      return '';
    },
  },
};

export const HOTEL_CONFIG = {
  defaultImage: '/static/icons/no-image.png',
  defaultPageSize: 15,
  mapSvgThumbnail: '/static/icons/map.svg',
  __getImageWithDefault(images: string[] | null | undefined) {
    if (images?.length) {
      return images[0];
    }
    return this.defaultImage;
  },
};

const getHeaderConfig = () => {
  if (APP_ENV === 'STAGING') {
    return {
      displayBookmark: true,
      displayBookingHistory: true,
      urls: [
        { text: 'Hotel', url: '/', target: undefined, isExternalUrl: false },
        {
          text: 'Day tour',
          url: 'https://stg2-new.ntaredballoon.jp/',
          isExternalUrl: true,
          target: '_blank',
          isSsoUrl: true,
        },
        {
          text: 'Package',
          url: 'https://stg2-new.ntaredballoon.jp/tour/',
          target: '_blank',
          isExternalUrl: true,
          isSsoUrl: true,
        },
      ],
    };
  }

  return {
    displayBookmark: true,
    displayBookingHistory: true,
    urls: [
      { text: 'Hotel', url: '/', target: undefined, isExternalUrl: false },
      {
        text: 'Day tour',
        url: 'https://new.ntaredballoon.jp/',
        isExternalUrl: true,
        target: '_blank',
        isSsoUrl: true,
      },
      {
        text: 'Package',
        url: 'https://new.ntaredballoon.jp/tour/',
        target: '_blank',
        isExternalUrl: true,
        isSsoUrl: true,
      },
    ],
  };
};
export const DefaultHeaderConfig = getHeaderConfig();

export const HOTEL_SEARCHBOX_CONFIG = {
  defaultChildAge: 8,
  maxPassengerPerRoom: 6,
  maxRoom: 3,
  defaultRoomValue: {
    roomIndex: 0,
    noOfAdult: 2,
    noOfChild: 0,
    childAges: [],
  },
  adultMinAge: 12,
  childrenMinAge: 6,
  childrenMaxAge: 11,
  note: 'Fees for infants aged 0 to 2 will be charged at the hotel counter based on room service demand.',
  defaultMealType: BoardType.BreakfastIncl,
  __createRoomKey: (index, room) => `${index}-${room?.noOfAdult}-${room?.noOfChild}-${room?.childAges?.join(',')}`,

  __minDate: (cutoffDays: number | null | undefined) => {
    let formattedDate = moment()
      .tz('Asia/Tokyo')
      .format('YYYY-MM-DD');
    return moment(formattedDate)
      .add(cutoffDays || 0, 'day')
      .toDate();
  },

  __defaultStartDate: (cutoffDays: number | null | undefined) => {
    return HOTEL_SEARCHBOX_CONFIG.__minDate(cutoffDays);
  },

  __defaultEndDate: (cutoffDays: number | null | undefined) => {
    let formattedDate = moment()
      .tz('Asia/Tokyo')
      .format('YYYY-MM-DD');
    return moment(formattedDate)
      .add(cutoffDays || 0, 'day')
      .add(1, 'day')
      .toDate();
  },
};

export const PrivacyPolicyURL = 'https://www.ntainbound.com/privacy-policy/';
export const TermsOfUseURL = '/terms-and-conditions';

export const HotelDisplayConfig = {
  RoomColor: {
    NTA: '#e5f0fd',
    Other: '#fdf5ee',
    __getRoomColor: function(providerName: string | null | undefined) {
      return providerName === 'NTA' ? this.NTA : this.Other;
    },
  },
};

export const SSO_URL_MAPPING = {
  miyabi: 'https://new.ntaredballoon.jp',
};

export const LogoutReturnUrlMapping = {
  miyabi: 'https://new.ntaredballoon.jp',
};
